import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, FileText, Map, ShoppingCart, Flame, AlertTriangle } from 'lucide-react';
import { T, glass, useIsMobile } from '../ui';
import { useGasData } from './gasData';
import type { GasView } from '../types';
import { Clientes } from './clientes';
import { Contratos } from './Contratos';
import { Rutas } from './Rutas';
import { Ventas } from './Ventas';
const NAV: {
  id: GasView;
  label: string;
  icon: React.ElementType;
}[] = [{
  id: 'clientes',
  label: 'Clientes',
  icon: Users
}, {
  id: 'contratos',
  label: 'Contratos',
  icon: FileText
}, {
  id: 'rutas',
  label: 'Rutas',
  icon: Map
}, {
  id: 'ventas',
  label: 'Ventas',
  icon: ShoppingCart
}];
function fmtRD(n: number) {
  return new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    maximumFractionDigits: 0
  }).format(n);
}
function MiniStat({
  label,
  value,
  accent
}: {
  label: string;
  value: string;
  accent: string;
}) {
  const isMobile = useIsMobile();
  return <div style={{
    flex: '1 1 150px',
    minWidth: isMobile ? '100%' : 150,
    padding: isMobile ? '14px 16px' : '16px 20px',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 16,
    background: 'rgba(255,255,255,0.06)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.12)',
    boxShadow: '0 8px 24px rgba(0,0,0,.25)'
  }} data-id="element-574" data-mp="22ngelrymytro">
      
      <div style={{
      position: 'absolute',
      right: -20,
      top: -20,
      width: 80,
      height: 80,
      borderRadius: '50%',
      background: `radial-gradient(circle, ${accent}3d 0%, transparent 70%)`,
      pointerEvents: 'none'
    }} data-id="element-575" data-mp="2qij29wlper6k" />
      
      <div style={{
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: accent,
      marginBottom: 8,
      position: 'relative'
    }} data-id="element-576" data-mp="30aotv581n6l8">
        
        {label}
      </div>
      <div style={{
      fontSize: isMobile ? 20 : 26,
      fontWeight: 800,
      letterSpacing: '-0.02em',
      fontFamily: "'Space Grotesk', sans-serif",
      color: '#FFF7EE',
      position: 'relative'
    }} data-id="element-577" data-mp="1a3zem0nxrwpg">
        
        {value}
      </div>
    </div>;
}
export default function GasApp() {
  const isMobile = useIsMobile();
  const [view, setView] = useState<GasView>('clientes');
  const data = useGasData();
  const stats = useMemo(() => {
    const clientesActivos = data.clientes.filter(c => c.estado === 'activo').length;
    const contratosVigentes = data.contratos.filter(c => c.estado === 'vigente').length;
    const rutasActivas = data.rutas.filter(r => r.estado === 'activa').length;
    const hoy = new Date();
    const ventasMes = data.ventas.filter(v => {
      const f = new Date(v.fecha);
      return f.getMonth() === hoy.getMonth() && f.getFullYear() === hoy.getFullYear();
    });
    const totalMes = ventasMes.reduce((acc, v) => acc + (v.total || 0), 0);
    return {
      clientesActivos,
      contratosVigentes,
      rutasActivas,
      totalMes
    };
  }, [data.clientes, data.contratos, data.rutas, data.ventas]);
  const pages: Record<GasView, React.ReactNode> = {
    clientes: <Clientes clientes={data.clientes} onAddCliente={data.onAddCliente} onDeleteCliente={data.onDeleteCliente} onUpdateCliente={data.onUpdateCliente} data-id="element-578" data-mp="3fr6vb1lk663x" />,
    contratos: <Contratos contratos={data.contratos} clientes={data.clientes} onAddContrato={data.onAddContrato} onDeleteContrato={data.onDeleteContrato} onUpdateContrato={data.onUpdateContrato} data-id="element-579" data-mp="35u27kxc2e2l0" />,
    rutas: <Rutas rutas={data.rutas} clientes={data.clientes} onAddRuta={data.onAddRuta} onDeleteRuta={data.onDeleteRuta} onUpdateRuta={data.onUpdateRuta} data-id="element-580" data-mp="2wtdrlx341s5h" />,
    ventas: <Ventas ventas={data.ventas} clientes={data.clientes} onAddVenta={data.onAddVenta} onDeleteVenta={data.onDeleteVenta} onUpdateVenta={data.onUpdateVenta} data-id="element-581" data-mp="31sgv3d3fzqbc" />
  };
  return <div style={{
    minHeight: '100vh',
    background: '#FFFCF8',
    color: '#1A1207',
    fontFamily: "'DM Sans',-apple-system,sans-serif"
  }} data-id="element-582" data-mp="zzsrh2mvz0v1">
      
      {/* ── HERO ─────────────────────────────────────────────────── */}
      <div style={{
      position: 'relative',
      overflow: 'hidden',
      background: 'radial-gradient(120% 160% at 15% 0%, #2A1000 0%, #150900 55%, #0D0600 100%)',
      color: '#FFF7EE',
      padding: isMobile ? '30px 18px 26px' : '44px 32px 34px'
    }} data-id="element-583" data-mp="2r5otbwmiqats">
        
        {/* glow orbs */}
        <div style={{
        position: 'absolute',
        top: -80,
        right: -60,
        width: 280,
        height: 280,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,107,1,.35) 0%, transparent 70%)',
        filter: 'blur(10px)',
        pointerEvents: 'none'
      }} data-id="element-584" data-mp="2d6fjdeowocy9" />
        
        <div style={{
        position: 'absolute',
        bottom: -100,
        left: '20%',
        width: 220,
        height: 220,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,180,60,.18) 0%, transparent 70%)',
        filter: 'blur(10px)',
        pointerEvents: 'none'
      }} data-id="element-585" data-mp="1uemvtzslvjxx" />
        

        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.45,
        ease: [0.2, 0.8, 0.2, 1]
      }} style={{
        position: 'relative',
        maxWidth: 1200,
        margin: '0 auto'
      }} data-id="element-586" data-mp="9vj7hansvcg7">
          
          <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 14
        }} data-id="element-587" data-mp="3idyt7ihjurcb">
            
            <div style={{
            width: 38,
            height: 38,
            borderRadius: 12,
            background: 'linear-gradient(135deg, #FF8C00 0%, #FF5A00 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 6px 20px rgba(255,107,1,.4)'
          }} data-id="element-588" data-mp="3rdc1i7txt9vg">
              
              <Flame size={19} color="#fff" strokeWidth={2.4} data-id="element-589" data-mp="33leseohvgvy1" />
            </div>
            <div style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'rgba(255,220,180,.75)'
          }} data-id="element-590" data-mp="arg6p4aghzjt">
              
              Gestión comercial
            </div>
          </div>

          <h1 style={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: isMobile ? 26 : 34,
          fontWeight: 800,
          letterSpacing: '-0.03em',
          margin: '0 0 6px'
        }} data-id="element-591" data-mp="fk9czwlkpfy0">
            
            Gas Industrial
          </h1>
          <p style={{
          fontSize: 13.5,
          color: 'rgba(255,247,238,.65)',
          margin: '0 0 24px',
          maxWidth: 520
        }} data-id="element-592" data-mp="v3eybyh1vrp">
            
            Clientes, contratos, rutas de distribución y ventas, todo en un
            solo lugar.
          </p>

          <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12
        }} data-id="element-593" data-mp="17bf8fzqcmpgy">
            
            <MiniStat label="Clientes activos" value={String(stats.clientesActivos)} accent="#FF8C00" data-id="element-594" data-mp="1fqv2bh5qio7e" />
            
            <MiniStat label="Contratos vigentes" value={String(stats.contratosVigentes)} accent="#5BBB4A" data-id="element-595" data-mp="1jv2pvb0ghy41" />
            
            <MiniStat label="Rutas activas" value={String(stats.rutasActivas)} accent="#38BDF8" data-id="element-596" data-mp="15vul82lixxsg" />
            
            <MiniStat label="Ventas del mes" value={fmtRD(stats.totalMes)} accent="#F5C542" data-id="element-597" data-mp="1yifex9ph2lz6" />
            
          </div>
        </motion.div>
      </div>

      {data.sinTablas && <div style={{
      background: '#FFF4E5',
      borderBottom: '1px solid #F5C542',
      padding: isMobile ? '10px 14px' : '10px 24px',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      fontSize: 12.5,
      color: '#7A4B00'
    }} data-id="element-598" data-mp="23gp9bi3dhijq">
        
        <AlertTriangle size={16} style={{
        flexShrink: 0
      }} data-id="element-599" data-mp="2hg8go8od0puz" />
        <span data-id="element-600" data-mp="2m8i4nj5wzfum">
          Estás trabajando solo en local: las tablas de Gas Industrial aún no
          existen en Supabase. Corre <strong data-id="element-601" data-mp="m6san6p2jrn2">gas_tablas.sql</strong> en el SQL
          Editor de tu proyecto para que estos registros se guarden ahí.
        </span>
      </div>}

      {/* ── TABS ─────────────────────────────────────────────────── */}
      <div style={{
      ...glass,
      position: 'sticky',
      top: 0,
      zIndex: 100,
      borderRadius: 0,
      borderLeft: 'none',
      borderRight: 'none',
      borderTop: 'none'
    }} data-id="element-602" data-mp="cg750zaz9mun">
        
        <nav style={{
        display: 'flex',
        gap: 4,
        maxWidth: 1200,
        margin: '0 auto',
        padding: isMobile ? '8px 14px' : '10px 24px',
        overflowX: isMobile ? 'auto' : 'visible',
        WebkitOverflowScrolling: 'touch'
      }} data-id="element-603" data-mp="152ie6ycngw32">
          
          {NAV.map(item => {
          const Icon = item.icon;
          const active = view === item.id;
          return <button key={item.id} onClick={() => setView(item.id)} style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            padding: '9px 16px',
            borderRadius: 10,
            border: 'none',
            background: 'transparent',
            color: active ? '#fff' : T.textMuted,
            fontSize: 13,
            fontWeight: active ? 700 : 600,
            whiteSpace: 'nowrap',
            flexShrink: 0,
            cursor: 'pointer',
            fontFamily: 'inherit',
            zIndex: 1
          }} data-id="element-604" data-mp="3gvgmg516dkmx">
                
                {active && <motion.div layoutId="gas-tab-active" transition={{
              type: 'spring',
              stiffness: 380,
              damping: 32
            }} style={{
              position: 'absolute',
              inset: 0,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #FF8C00 0%, #FF5A00 100%)',
              boxShadow: '0 4px 16px rgba(255,107,1,.35)',
              zIndex: -1
            }} data-id="element-605" data-mp="2f2ci3odcs21r" />}
                <Icon size={15} data-id="element-606" data-mp="tprrb4l5b52q" />
                {item.label}
              </button>;
        })}
        </nav>
      </div>

      {/* ── CONTENT ──────────────────────────────────────────────── */}
      <main style={{
      padding: isMobile ? '18px 14px 48px' : '28px 24px 60px',
      maxWidth: 1200,
      margin: '0 auto'
    }} data-id="element-607" data-mp="1ixefh2aqf8kk">
        
        <AnimatePresence mode="wait" data-id="element-608" data-mp="1susl06v4acy3">
          <motion.div key={view} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.2,
          ease: [0.2, 0.8, 0.2, 1]
        }} data-id="element-609" data-mp="1svvaqbgpe9cf">
            
            {pages[view]}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>;
}